import { useEffect } from 'react';
import { isValidFlatfinderPolygon, type FlatfinderRegion } from 'realitiq-utils/src/flatfinder';
import { z } from 'zod';

const areaClipboardSchema = z.object({
  type: z.literal('realitiq-flatfinder-area'),
  version: z.literal(1),
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  points: z
    .array(z.object({ x: z.number().min(0).max(1), y: z.number().min(0).max(1) }))
    .min(3)
    .max(200)
    .refine(isValidFlatfinderPolygon),
});

function isTextFieldOrDialog(target: EventTarget | null) {
  return (
    target instanceof Element &&
    Boolean(
      target.closest(
        'input, textarea, select, [contenteditable]:not([contenteditable="false"]), [role="textbox"], [role="dialog"], [role="alertdialog"]'
      )
    )
  );
}

export function useAreaClipboard({
  enabled,
  selectedArea,
  regions,
  onChange,
  onSelect,
}: {
  enabled: boolean;
  selectedArea?: FlatfinderRegion;
  regions: FlatfinderRegion[];
  onChange: (regions: FlatfinderRegion[]) => void;
  onSelect: (id: string) => void;
}) {
  useEffect(() => {
    if (!enabled) return;

    function shouldIgnoreClipboardEvent(event: ClipboardEvent) {
      return (
        event.defaultPrevented ||
        !event.clipboardData ||
        isTextFieldOrDialog(event.target) ||
        isTextFieldOrDialog(document.activeElement)
      );
    }

    function copySelectedArea(event: ClipboardEvent) {
      if (
        shouldIgnoreClipboardEvent(event) ||
        !selectedArea ||
        window.getSelection()?.isCollapsed === false
      )
        return;

      // Only geometry and color belong on the clipboard, never an estate link.
      event.clipboardData?.setData(
        'text/plain',
        JSON.stringify({
          type: 'realitiq-flatfinder-area',
          version: 1,
          color: selectedArea.color,
          points: selectedArea.points,
        })
      );
      event.preventDefault();
    }

    function pasteCopiedArea(event: ClipboardEvent) {
      if (shouldIgnoreClipboardEvent(event)) return;
      const text = event.clipboardData?.getData('text/plain');
      if (!text || text.length > 20000) return;

      let payload: unknown;
      try {
        payload = JSON.parse(text);
      } catch {
        return;
      }
      const result = areaClipboardSchema.safeParse(payload);
      if (!result.success) return;

      const region: FlatfinderRegion = {
        id: crypto.randomUUID(),
        estateId: null,
        color: result.data.color,
        points: result.data.points,
        sortOrder: regions.length,
      };
      event.preventDefault();
      onChange([...regions, region]);
      onSelect(region.id);
    }

    document.addEventListener('copy', copySelectedArea);
    document.addEventListener('paste', pasteCopiedArea);
    return () => {
      document.removeEventListener('copy', copySelectedArea);
      document.removeEventListener('paste', pasteCopiedArea);
    };
  }, [enabled, onChange, onSelect, regions, selectedArea]);
}
